import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { CreceLogo } from "@/components/CreceLogo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      if (data.dev_mode && data.dev_reset_link) setDevLink(data.dev_reset_link);
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-stone-50 p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8">
          <CreceLogo size="md" theme="dark" />
          <span className="font-display font-black text-2xl">Crece</span>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200 p-8 shadow-sm">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Password Recovery</span>
          <h1 className="font-display text-3xl font-black tracking-tight mt-2">Forgot your password?</h1>
          {!sent ? (
            <>
              <p className="text-sm text-stone-500 mt-2">Enter your email — we&apos;ll send you a link to set a new password.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label className="font-semibold text-sm">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-3.5 w-4 h-4 text-stone-500" />
                    <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
                      placeholder="you@example.com" className="pl-10 h-12 rounded-lg border-stone-300" />
                  </div>
                </div>
                <Button type="submit" data-testid="forgot-submit" disabled={loading}
                  className="w-full h-12 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
                  {loading ? "Sending..." : "Send reset link"} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </>
          ) : (
            <div className="mt-4 space-y-4" data-testid="forgot-sent-state">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm">
                If an account with <b>{email}</b> exists, we&apos;ve sent a reset link. Check your inbox (and spam folder).
              </div>
              {devLink && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs">
                  <div className="font-bold uppercase tracking-wider text-amber-800 mb-1">Dev mode (no Gmail keys yet)</div>
                  <a href={devLink} className="font-mono text-emerald-900 underline break-all" data-testid="dev-reset-link">{devLink}</a>
                </div>
              )}
            </div>
          )}
          <div className="mt-6 pt-5 border-t border-stone-200 text-center">
            <Link to="/auth" className="inline-flex items-center gap-1.5 text-sm font-bold text-stone-700 hover:text-emerald-900" data-testid="back-to-login">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
